import React, { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Phone,
  Gavel,
  Calculator,
  Scale,
  CheckCircle2,
  ChevronRight,
} from "lucide-react";
import { Header } from "../sections/Header";
import { Footer } from "../sections/Footer";
import { StickyCTA } from "../sections/StickyCTA";
import {
  FIRM,
  PRACTICE_AREA_CATEGORIES,
  PRACTICE_AREA_DETAILS,
} from "../data/mock";
import {
  Seo,
  buildBreadcrumbSchema,
  buildWebPageSchema,
  buildServiceSchema,
  ORGANIZATION_SCHEMA,
} from "../components/Seo";

const ICONS = { Gavel, Calculator, Scale };

const PracticeAreaDetail = () => {
  const { slug } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const category = PRACTICE_AREA_CATEGORIES.find((c) => c.slug === slug);
  const detail = PRACTICE_AREA_DETAILS[slug];

  if (!category || !detail) {
    return <Navigate to="/practice-areas" replace />;
  }

  const Icon = ICONS[category.icon] || Scale;
  const related = (detail.related || [])
    .map((r) => PRACTICE_AREA_CATEGORIES.find((c) => c.slug === r))
    .filter(Boolean);

  const pagePath = `/practice-areas/${slug}`;
  const SEO_TITLES = {
    litigation: "Forensic Accounting Litigation Attorney Dallas | Texas",
    consulting: "Forensic Accounting Consultant & Expert Witness — Texas",
    "non-litigation": "Business Formation Attorney Texas — CPA & Estate Planning",
  };
  const pageTitle = SEO_TITLES[slug] || `${category.title} — Practice Area`;
  const pageDesc = `${category.tagline} ${detail.extendedIntro.slice(0, 140)}...`;

  return (
    <>
      <Seo
        title={pageTitle}
        description={pageDesc}
        path={pagePath}
        keywords={`${category.title.toLowerCase()} Texas, ${category.items.slice(0, 4).join(", ").toLowerCase()}`}
        schemas={[
          ORGANIZATION_SCHEMA,
          buildServiceSchema({
            name: category.title,
            description: detail.extendedIntro,
            slug,
          }),
          buildWebPageSchema({
            title: pageTitle,
            description: pageDesc,
            path: pagePath,
          }),
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Practice Areas", path: "/practice-areas" },
            { name: category.title, path: pagePath },
          ]),
        ]}
      />
      <main data-testid="practice-detail-page" className="bg-ivory min-h-screen">
      <Header />

      {/* Hero */}
      <section
        data-testid="detail-hero"
        className="relative pt-36 lg:pt-44 pb-16 lg:pb-20 bg-ivory overflow-hidden"
      >
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, #102845 1px, transparent 0)",
              backgroundSize: "32px 32px",
            }}
          />
        </div>
        <div className="absolute top-20 -right-40 w-[500px] h-[500px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          {/* Breadcrumb */}
          <nav
            data-testid="detail-breadcrumb"
            className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-navy/60 mb-8 fade-up"
          >
            <Link to="/" className="hover:text-navy transition-colors">
              Home
            </Link>
            <ChevronRight className="w-3 h-3" strokeWidth={2} />
            <Link to="/practice-areas" className="hover:text-navy transition-colors">
              Practice Areas
            </Link>
            <ChevronRight className="w-3 h-3" strokeWidth={2} />
            <span className="text-navy font-medium">{category.title.split(" ").slice(0, 2).join(" ")}</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-9">
              <div className="flex items-center gap-3 mb-6 fade-up-delay-1">
                <div className="gold-line" />
                <span className="text-xs tracking-[0.3em] uppercase text-navy/70 font-medium">
                  {category.eyebrow} · Practice Area
                </span>
              </div>
              <h1
                data-testid="detail-headline"
                className="font-serif text-5xl sm:text-6xl lg:text-7xl text-navy leading-[1.04] tracking-tight fade-up-delay-2"
              >
                {category.title}
                <span className="text-gold">.</span>
              </h1>
              <p
                data-testid="detail-tagline"
                className="mt-6 font-serif italic text-xl lg:text-2xl text-gold leading-snug max-w-3xl fade-up-delay-3"
              >
                {category.tagline}
              </p>
            </div>
            <div className="lg:col-span-3 flex lg:justify-end fade-up-delay-3">
              <div className="w-20 h-20 rounded-sm bg-navy/5 flex items-center justify-center">
                <Icon className="w-9 h-9 text-navy" strokeWidth={1.25} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Extended intro */}
      <section
        data-testid="detail-intro"
        className="py-16 lg:py-20 bg-ivory border-t border-[#C0974E]/15"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <p className="font-serif text-2xl lg:text-3xl text-navy leading-snug">
            {detail.extendedIntro}
          </p>
        </div>
      </section>

      {/* Representative engagements */}
      <section
        data-testid="detail-engagements"
        className="py-20 lg:py-28 bg-cream"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <div className="gold-line" />
              <span className="text-xs tracking-[0.3em] uppercase text-navy/70 font-medium">
                Representative Engagements
              </span>
            </div>
            <h2 className="font-serif text-3xl lg:text-4xl text-navy leading-[1.1] tracking-tight">
              What this work
              <br />
              <span className="italic text-gold">looks like</span> in practice.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <ul className="space-y-px">
              {category.items.map((item, idx) => (
                <li
                  key={idx}
                  data-testid={`engagement-${idx}`}
                  className="flex items-start gap-5 py-5 lg:py-6 border-b border-[#C0974E]/20 group hover:border-gold/50 transition-colors"
                >
                  <div className="pt-1.5 flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-gold" strokeWidth={1.5} />
                  </div>
                  <span className="text-base lg:text-lg font-serif text-navy leading-snug pt-0.5">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* When to engage */}
      <section
        data-testid="detail-when"
        className="py-20 lg:py-28 bg-ivory"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-14">
            <div className="flex items-center gap-3 mb-5">
              <div className="gold-line" />
              <span className="text-xs tracking-[0.3em] uppercase text-navy/70 font-medium">
                When Clients Engage Us
              </span>
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl text-navy leading-[1.05] tracking-tight">
              The shape of a call
              <br />
              that <span className="italic text-gold">should</span> come in.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {detail.whenToEngage.map((point, idx) => (
              <div
                key={idx}
                data-testid={`when-${idx}`}
                className="group bg-white border border-[#C0974E]/20 p-8 lg:p-10 hover:border-gold/60 transition-all duration-500 hover:shadow-lg hover:shadow-navy/5"
              >
                <span className="font-serif text-4xl text-gold/30">0{idx + 1}</span>
                <h3 className="font-serif text-xl lg:text-2xl text-navy mt-4 mb-3 leading-tight">
                  {point.title}
                </h3>
                <p className="text-base text-navy/75 leading-relaxed">
                  {point.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why it matters (dark) */}
      <section
        data-testid="detail-why"
        className="py-20 lg:py-28 bg-navy text-ivory relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, #C0974E 1px, transparent 0)",
              backgroundSize: "40px 40px",
            }}
          />
        </div>

        <div className="relative max-w-5xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="gold-line" />
            <span className="text-xs tracking-[0.3em] uppercase text-gold font-medium">
              Why It Matters
            </span>
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl text-ivory leading-[1.05] tracking-tight max-w-3xl">
            {detail.whyItMatters.title}
          </h2>
          <p className="mt-8 text-lg lg:text-xl text-ivory/85 leading-relaxed max-w-3xl">
            {detail.whyItMatters.content}
          </p>
          {detail.notHandled && (
            <p className="mt-10 pt-6 border-t border-ivory/15 text-sm italic text-ivory/55 max-w-2xl">
              {detail.notHandled}
            </p>
          )}
        </div>
      </section>

      {/* Related practice areas */}
      {related.length > 0 && (
        <section
          data-testid="detail-related"
          className="py-20 lg:py-24 bg-cream"
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="gold-line" />
              <span className="text-xs tracking-[0.3em] uppercase text-navy/70 font-medium">
                Related Practice Areas
              </span>
            </div>
            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
              {related.map((rel) => {
                const RelIcon = ICONS[rel.icon] || Scale;
                return (
                  <Link
                    key={rel.slug}
                    to={`/practice-areas/${rel.slug}`}
                    data-testid={`related-${rel.slug}`}
                    className="group block bg-white border border-[#C0974E]/20 p-8 lg:p-10 hover:border-gold/60 transition-all duration-500 hover:shadow-xl hover:shadow-navy/5"
                  >
                    <div className="flex items-start justify-between mb-5">
                      <div className="w-12 h-12 rounded-sm bg-navy/5 flex items-center justify-center group-hover:bg-navy group-hover:text-gold transition-all">
                        <RelIcon
                          className="w-5 h-5 text-navy group-hover:text-gold transition-colors"
                          strokeWidth={1.5}
                        />
                      </div>
                      <ArrowUpRight
                        className="w-5 h-5 text-navy/40 group-hover:text-gold group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                        strokeWidth={1.5}
                      />
                    </div>
                    <h3 className="font-serif text-2xl text-navy leading-tight mb-2">
                      {rel.title}
                    </h3>
                    <p className="text-sm text-navy/70 leading-relaxed">
                      {rel.tagline}
                    </p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section
        data-testid="detail-cta"
        className="py-20 lg:py-28 bg-ivory"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="inline-flex items-center gap-3 mb-6 justify-center">
            <div className="gold-line" />
            <span className="text-xs tracking-[0.3em] uppercase text-navy/70 font-medium">
              Start the Conversation
            </span>
            <div className="gold-line" />
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl text-navy leading-[1.05] tracking-tight">
            Is your matter a fit for
            <br />
            <span className="italic text-gold">{category.title.split(" ").slice(0, 3).join(" ")}</span>?
          </h2>
          <p className="mt-6 text-base lg:text-lg text-navy/75 max-w-2xl mx-auto leading-relaxed">
            Send Dick a brief description. He'll tell you straight whether he
            can help — and if not, where to look.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a
              href="/#contact"
              data-testid="detail-cta-primary"
              className="btn-primary inline-flex items-center gap-3 px-7 py-4 text-xs tracking-[0.25em] uppercase font-medium group"
            >
              Schedule a Consultation
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href={`tel:${FIRM.phone.replace(/[^\d+]/g, "")}`}
              data-testid="detail-cta-phone"
              className="inline-flex items-center gap-3 px-2 py-4 text-sm text-navy font-medium hover:text-gold transition-colors"
            >
              <Phone className="w-4 h-4" strokeWidth={2} />
              <span className="link-underline">{FIRM.phone}</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <StickyCTA />
    </main>
    </>
  );
};

export default PracticeAreaDetail;
